import { cn } from "@/lib/cn"
import { Link } from "@inertiajs/react"
import type { ReactNode, ButtonHTMLAttributes, AnchorHTMLAttributes } from "react"

type Variant = "primary" | "secondary" | "ghost" | "outline" | "brand" | "invert"
type Size = "sm" | "md" | "lg"

const variants: Record<Variant, string> = {
  primary:
    "bg-ink-950 text-white ring-1 ring-ink-950 hover:bg-ink-800 hover:ring-ink-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
  secondary:
    "bg-white text-ink-950 ring-1 ring-ink-200 hover:bg-ink-50 hover:ring-ink-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
  ghost:
    "bg-transparent text-ink-700 hover:text-ink-950 hover:bg-ink-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
  outline:
    "bg-transparent text-ink-950 ring-1 ring-ink-300 hover:bg-ink-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
  brand:
    "bg-brand-500 text-ink-950 ring-1 ring-brand-500 hover:bg-brand-400 hover:ring-brand-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white",
  invert:
    "bg-white/0 text-white ring-1 ring-white/20 hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white",
}

const sizes: Record<Size, string> = {
  sm: "text-sm px-3 py-1.5 rounded-lg gap-x-1.5",
  md: "text-sm px-4 py-2.5 rounded-xl gap-x-2",
  lg: "text-base px-5 py-3 rounded-xl gap-x-2",
}

const base =
  "inline-flex items-center justify-center font-medium tracking-tight transition select-none disabled:opacity-50 disabled:pointer-events-none"

type CommonProps = {
  variant?: Variant
  size?: Size
  className?: string
  children: ReactNode
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  // Disables the tactile scale-on-press — use where a shrinking button would be
  // distracting (e.g. inside an element that is itself animating).
  static?: boolean
}

type ButtonAsButton = CommonProps & {
  as?: "button"
} & ButtonHTMLAttributes<HTMLButtonElement>

type ButtonAsLink = CommonProps & {
  as: "link"
  href: string
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href">

type ButtonAsAnchor = CommonProps & {
  as: "a"
  href: string
} & AnchorHTMLAttributes<HTMLAnchorElement>

export type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor

export function Button(props: ButtonProps) {
  const {
    variant = "primary",
    size = "md",
    className,
    children,
    leadingIcon,
    trailingIcon,
    static: isStatic = false,
    as: _as,
    ...rest
  } = props as CommonProps & { as?: "button" | "link" | "a"; href?: string } & Record<string, unknown>

  const paddingFix =
    leadingIcon && size === "md"
      ? "pl-3"
      : trailingIcon && size === "md"
        ? "pr-3"
        : leadingIcon && size === "lg"
          ? "pl-4"
          : trailingIcon && size === "lg"
            ? "pr-4"
            : leadingIcon && size === "sm"
              ? "pl-2"
              : trailingIcon && size === "sm"
                ? "pr-2"
                : ""

  const press = isStatic ? "" : "active:scale-[0.96] motion-reduce:active:scale-100"

  const classes = cn(base, variants[variant], sizes[size], paddingFix, press, className)

  const content = (
    <>
      {leadingIcon}
      {children}
      {trailingIcon}
    </>
  )

  if (_as === "link") {
    const { href, ...linkRest } = rest as { href: string }
    return (
      <Link href={href} className={classes} {...linkRest}>
        {content}
      </Link>
    )
  }

  if (_as === "a") {
    const { href, ...anchorRest } = rest as { href: string }
    return (
      <a href={href} className={classes} {...anchorRest}>
        {content}
      </a>
    )
  }

  return (
    <button className={classes} {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}>
      {content}
    </button>
  )
}
